//User tries to change password

// Library utilities
import React, { useState } from "react";
import PortalOverlay from "../../../../Shared/portal-overlay/portalOverlay";
// Components
import ErrorMessage from "../../../../Shared/error-message/errorMessage";
import Input from "../../../../Shared/input/input";
import Button from "../../../../Shared/button/button";
import Toast from "../../../../Shared/toast/toast";

// Styles
import Styles from "../../../../Shared/popup.module.scss";
import {
  cancelButtonStyle,
  primaryButtonStyle,
  cancelButtonHoverStyle,
  primaryButtonHoverStyle,
} from "../../../../Shared/buttonStyles";
import { ACCENTCOLOR, SECONDARYCOLOR } from "../../../../Shared/colors";

// Functions
import { checkIsEmpty } from "../../../../utils/validation";
import { callApi } from "../../../../api/fetch";
import {
  meetsPasswordPolicy,
  PASSWORD_POLICY_ERROR,
} from "../../../newOnboarding/constants/validationRules";

// Constants
import { MEDIUM } from "../../../../Shared/buttonSize";
import PasswordEye from "../../../../Shared/svg/Eye";
import { testIdProps } from "../../../../utils/testIdProps";

function ChangePassword({ onCancel, onPasswordChangeSuccess }: any) {
  const [error, setError] = useState({
    currentPasswordError: "",
    newPasswordError: "",
    confirmPasswordError: "",
  });
  const [validData, setValidData] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [password, setPassword] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  //Validating functions for all the input fields
  function checkCurrentPassword() {
    if (checkIsEmpty(password.currentPassword)) {
      setError((error) => ({
        ...error,
        currentPasswordError: "Current Password cannot be empty",
      }));
      setValidData((validData) => ({
        ...validData,
        currentPassword: false,
      }));
      return false;
    }
    return true;
  }
  function checkNewPassword() {
    if (checkIsEmpty(password.newPassword)) {
      setError((error) => ({
        ...error,
        newPasswordError: "New Password cannot be empty",
      }));
      setValidData((validData) => ({
        ...validData,
        newPassword: false,
      }));
      return false;
    }
    if (!meetsPasswordPolicy(password.newPassword)) {
      setError((error) => ({
        ...error,
        newPasswordError: PASSWORD_POLICY_ERROR,
      }));
      setValidData((validData) => ({
        ...validData,
        newPassword: false,
      }));
      return false;
    }
    return true;
  }
  function checkConfirmPassword() {
    if (checkIsEmpty(password.confirmPassword)) {
      setError((error) => ({
        ...error,
        confirmPasswordError: "Confirm Password cannot be empty",
      }));
      setValidData((validData) => ({
        ...validData,
        confirmPassword: false,
      }));
      return false;
    }
    if (password.newPassword !== password.confirmPassword) {
      setError((error) => ({
        ...error,
        confirmPasswordError: "Confirm Password does not match",
      }));
      setValidData((validData) => ({
        ...validData,
        confirmPassword: false,
      }));
      return false;
    }
    return true;
  }
  function handleCancelClicked(event: any) {
    event.preventDefault();
    onCancel();
    return;
  }
  async function handleChangePassword(event: any) {
    event.preventDefault();
    const allCheckPassed = [
      checkCurrentPassword(),
      checkNewPassword(),
      checkConfirmPassword(),
    ].every(Boolean);
    if (!allCheckPassed) {
      return;
    }
    setLoading(true);
    try {
      await callApi("/user/change-password", {
        method: "PUT",
        data: {
          current_password: password.currentPassword,
          new_password: password.newPassword,
        },
      });
      setLoading(false);
      setShowNewPassword(false);
      setShowCurrentPassword(false);
      setShowConfirmPassword(false);
      // Close the change password modal first
      onCancel();
      // Then trigger showing the reauth modal in parent
      if (onPasswordChangeSuccess) {
        onPasswordChangeSuccess();
      }
    } catch (err) {
      setLoading(false);
      toastTimeout(true, "error", (err as Error).message);
    }
    return;
  }

  function checkFormValidation() {
    return (
      loading ||
      password.currentPassword === "" ||
      error.currentPasswordError !== "" ||
      password.newPassword === "" ||
      error.newPasswordError !== "" ||
      password.confirmPassword === "" ||
      error.confirmPasswordError !== ""
    );
  }
  return (
    <PortalOverlay>
      <div
      className={`d-flex align-items-center justify-content-center ${Styles.overlay}`}
    >
      <div style={{ zIndex: "9999" }} className={`p-4 ${Styles.popUpContent}`}>
        <form onSubmit={handleChangePassword}>
          <div
            className="form-group mb-0 py-1"
            style={{ position: "relative" }}
          >
            <Input
              type={showCurrentPassword ? "text" : "password"}
              name="currentPassword"
              id="my-account-current-password"
              autoComplete="off"
              maxLength={15}
              placeholder="Current Password"
              requiredAstrict
              hasError={error.currentPasswordError}
              isValid={validData.currentPassword}
              labelname="Current Password"
              clear={false}
              onChange={(event: any) => {
                const value = event.target.value;
                setPassword((password) => ({
                  ...password,
                  currentPassword: value,
                }));
                setError((error) => ({
                  ...error,
                  currentPasswordError: "",
                }));
                setValidData((validData) => ({
                  ...validData,
                  currentPassword: true,
                }));
              }}
              onBlur={checkCurrentPassword}
            />
            <div
              {...testIdProps("my-account-show-current-password")}
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className={Styles.eye}
              style={
                error.currentPasswordError
                  ? { bottom: "46px" }
                  : { bottom: "21px" }
              }
            >
              <PasswordEye
                color={showCurrentPassword ? ACCENTCOLOR : SECONDARYCOLOR}
              />
            </div>
            <div className="d-flex align-items-center">
              {error.currentPasswordError && (
                <ErrorMessage>{error.currentPasswordError}</ErrorMessage>
              )}
            </div>
          </div>
          <div
            className="form-group mb-0 py-1"
            style={{ position: "relative" }}
          >
            <Input
              type={showNewPassword ? "text" : "password"}
              name="newPassword"
              id="my-account-new-password"
              autoComplete="off"
              maxLength={15}
              placeholder="New Password"
              requiredAstrict
              hasError={error.newPasswordError}
              isValid={validData.newPassword}
              labelname="New Password"
              clear={false}
              onChange={(event: any) => {
                const value = event.target.value;
                setPassword((password) => ({
                  ...password,
                  newPassword: value,
                }));
                setError((error) => ({
                  ...error,
                  newPasswordError: "",
                }));
                setValidData((validData) => ({
                  ...validData,
                  newPassword: true,
                }));
              }}
              onBlur={checkNewPassword}
            />
            <div
              {...testIdProps("my-account-show-new-password")}
              onClick={() => setShowNewPassword(!showNewPassword)}
              className={Styles.eye}
              style={
                error.newPasswordError ? { bottom: "46px" } : { bottom: "21px" }
              }
            >
              <PasswordEye
                color={showNewPassword ? ACCENTCOLOR : SECONDARYCOLOR}
              />
            </div>
            <div className="d-flex align-items-center">
              {error.newPasswordError && (
                <ErrorMessage>{error.newPasswordError}</ErrorMessage>
              )}
            </div>
          </div>
          <div
            className="form-group mb-0 py-1"
            style={{ position: "relative" }}
          >
            <Input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              id="my-account-confirm-password"
              autoComplete="off"
              maxLength={15}
              placeholder="Confirm Password"
              requiredAstrict
              hasError={error.confirmPasswordError}
              isValid={validData.confirmPassword}
              labelname="Confirm Password"
              clear={false}
              onChange={(event: any) => {
                const value = event.target.value;
                setPassword((password) => ({
                  ...password,
                  confirmPassword: value,
                }));
                setError((error) => ({
                  ...error,
                  confirmPasswordError: "",
                }));
                setValidData((validData) => ({
                  ...validData,
                  confirmPassword: true,
                }));
              }}
              onBlur={checkConfirmPassword}
            />
            <div
              {...testIdProps("my-account-show-confirm-password")}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={Styles.eye}
              style={
                error.confirmPasswordError
                  ? { bottom: "46px" }
                  : { bottom: "21px" }
              }
            >
              <PasswordEye
                color={showConfirmPassword ? ACCENTCOLOR : SECONDARYCOLOR}
              />
            </div>
            <div className="d-flex align-items-center">
              {error.confirmPasswordError && (
                <ErrorMessage>{error.confirmPasswordError}</ErrorMessage>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-around pb-2 pt-4">
            <div className="px-2">
              <Button
                size={MEDIUM}
                disabled={loading}
                hoveredStyle={cancelButtonHoverStyle}
                style={cancelButtonStyle}
                id="my-account-change-password-cancel"
                onClick={handleCancelClicked}
                type="button"
              >
                Cancel
              </Button>
            </div>
            <div className="px-2">
              <Button
                size={MEDIUM}
                disabled={checkFormValidation()}
                isLoading={loading}
                hoveredStyle={primaryButtonHoverStyle}
                style={primaryButtonStyle}
                id="my-account-change-password-save"
                onClick={handleChangePassword}
                type="submit"
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div></PortalOverlay>
  );
}

export default ChangePassword;
